<template>
  <nav class="navbar" v-if="currentRegion">
    <div class="nav-container">
      <router-link :to="`/${currentRegion}/home`" class="nav-logo">
        <img :src="IMG_PATHS.LOGO" alt="Logo" />
      </router-link>
      <ul class="nav-menu">
        <li class="nav-item"
            v-for="(item, index) in resolvedNavItems"
            :key="index"
            @mouseenter="openDropdown(index)"
            @mouseleave="closeDropdown()">
          <router-link
            :to="item.path"
            class="nav-link"
            v-if="!item.children || item.children.length === 0"
            @click="handleNavigation(item, $event)">
            {{ item.title }}
          </router-link>
          <div class="nav-link dropdown-toggle" v-else>
            {{ item.title }}
            <span class="arrow" :class="{ 'rotated': activeDropdown === index }"></span>
          </div>

          <!-- 下拉菜单 -->
          <transition name="slide">
            <div class="dropdown-menu" v-if="activeDropdown === index && item.children && item.children.length > 0">
              <router-link
                v-for="(child, childIndex) in item.children"
                :key="childIndex"
                :to="child.path || '#'"
                class="dropdown-item"
                @click="handleNavigation(child, $event)"
              >
                {{ child.title }}
              </router-link>
            </div>
          </transition>
        </li>

        <!-- 区域切换按钮 -->
        <li class="nav-item">
          <span
            class="nav-link region-switch"
            @click="switchRegion"
          >
            {{ alternateRegionName }}
          </span>
        </li>

        <!-- 语言切换 -->
        <li class="nav-item">
          <div class="language-switcher">
            <select v-model="currentLanguage" @change="changeLanguage" class="language-select">
              <option value="zh">中文</option>
              <option value="en">English</option>
              <option value="ja">日本語</option>
            </select>
          </div>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script>
import { h } from 'vue'
import { ElMessage } from 'element-plus'
import { IMG_PATHS } from '@/config/cdnConfig.js'
import { getRegionConfig, getAvailableRegions } from '@/config/region/index.js'

export default {
  data() {
    return {
      currentLanguage: localStorage.getItem('language') || 'zh',
      activeDropdown: -1,
      IMG_PATHS,
      lastClickTime: 0,
      clickDelay: 2000
    }
  },
  computed: {
    // 从路由路径直接推导当前区域
    currentRegion() {
      const path = this.$route.path
      if (path.startsWith('/cn')) return 'cn'
      if (path.startsWith('/global')) return 'global'
      return null
    },
    // 当前区域配置
    config() {
      if (!this.currentRegion) return null
      return getRegionConfig(this.currentRegion)
    },
    // 从区域配置解析导航项
    resolvedNavItems() {
      if (!this.config || !this.config.navItems) return []
      return this.config.navItems.map(item => ({
        ...item,
        title: item.titleKey ? this.$t(item.titleKey) : item.title,
        children: (item.children || []).map(child => ({
          ...child
        }))
      }))
    },
    // 另一个区域
    alternateRegion() {
      return this.currentRegion === 'cn' ? 'global' : 'cn'
    },
    // 另一个区域的路径
    alternatePath() {
      return `/${this.alternateRegion}/home`
    },
    // 另一个区域的显示名
    alternateRegionName() {
      const regions = getAvailableRegions()
      const region = regions.find(r => r.code === this.alternateRegion)
      return region ? region.name : this.alternateRegion
    }
  },
  methods: {
    changeLanguage() {
      this.$i18n.locale = this.currentLanguage
      localStorage.setItem('language', this.currentLanguage)
    },
    switchRegion() {
      const targetRegion = this.alternateRegion
      localStorage.setItem('user_region', targetRegion)
      // 根据目标区域自动切换默认语言
      const config = getRegionConfig(targetRegion)
      const defaultLocale = config.defaultLocale || (targetRegion === 'cn' ? 'zh' : 'en')
      this.$i18n.locale = defaultLocale
      this.currentLanguage = defaultLocale
      localStorage.setItem('language', defaultLocale)
      this.$router.push(`/${targetRegion}/home`)
    },
    openDropdown(index) {
      this.activeDropdown = index
    },
    closeDropdown() {
      this.activeDropdown = -1
    },
    handleNavigation(item, event) {
      const currentTime = new Date().getTime()
      if (currentTime - this.lastClickTime < this.clickDelay) {
        event.preventDefault()
        return
      }
      this.lastClickTime = currentTime

      if (!item.path || item.path === '' || item.path === '#') {
        event.preventDefault()
        ElMessage({
          message: h('p', { style: 'line-height: 1; font-size: 14px' }, [
            h('span', null, '敬请期待！'),
          ]),
        })
      }
    }
  }
}
</script>

<style scoped>
.navbar {
  background-color: rgb(0, 0, 0, 0.8);
  height: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.2rem;
  box-shadow: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 999;
}

.nav-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 50px;
}

.nav-logo img {
  margin-top: 10px;
  height: 80px;
}

.nav-menu {
  display: flex;
  list-style: none;
  text-align: center;
  position: relative;
}

.nav-item {
  margin-left: 20px;
  position: relative;
}

.nav-link {
  color: #ffffff;
  text-decoration: none;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.nav-link:hover {
  color: #4ecca3;
}

.region-switch {
  color: #4ecca3;
  font-size: 0.9rem;
  border: 1px solid #4ecca3;
  border-radius: 4px;
  padding: 0.3rem 0.8rem;
}

.region-switch:hover {
  background-color: #4ecca3;
  color: #000;
}

.dropdown-toggle {
  cursor: pointer;
}

.arrow {
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 5px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #ffffff;
  transition: transform 0.3s ease;
}

.arrow.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  background-color: rgba(0, 0, 0, 0.9);
  min-width: 200px;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  padding: 10px 0;
  z-index: 1000;
}

.dropdown-item {
  display: block;
  padding: 10px 20px;
  color: #ffffff;
  text-decoration: none;
  transition: all 0.3s ease;
}

.dropdown-item:hover {
  background-color: rgba(78, 204, 163, 0.3);
  color: #4ecca3;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.language-switcher {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 1rem;
}

.language-select {
  background-color: #f0f0f0;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 5px;
  outline: none;
}

@media screen and (max-width: 768px) {
  .nav-container {
    padding: 0 20px;
  }

  .nav-menu {
    flex-direction: column;
    position: absolute;
    right: 0;
    top: 80px;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 20px;
    border-radius: 8px;
    transform: translateY(-10px);
    opacity: 0;
    pointer-events: none;
    transition: all 0.3s ease;
  }

  .nav-menu.active {
    transform: translateY(0);
    opacity: 1;
    pointer-events: auto;
  }

  .nav-item {
    margin: 10px 0;
  }

  .dropdown-menu {
    position: static;
    background-color: rgba(255, 255, 255, 0.9);
    box-shadow: none;
    border-radius: 0;
    padding-left: 20px;
  }

  .dropdown-item {
    color: #333;
  }

  .dropdown-item:hover {
    background-color: rgba(78, 204, 163, 0.1);
    color: #4ecca3;
  }
}
</style>
